<script setup lang="ts">
import { de as nuxtDe, en as nuxtEn } from '@nuxt/ui/locale'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import kitBannerUrl from '@/assets/images/kit_bg_banner.webp'

import AppConfirmDialog from './components/common/AppConfirmDialog.vue'
import GlobalLoader from './components/GlobalLoader.vue'
import AppMenu from './components/layout/AppMenu.vue'
import AppTopbar from './components/layout/AppTopbar.vue'
import AppVersion from './components/layout/AppVersion.vue'
import BreadcrumbBar from './components/layout/BreadcrumbBar.vue'
import EnvironmentIndicator from './components/layout/EnvironmentIndicator.vue'

const { locale, t } = useI18n()
const nuxtLocale = computed(() => (locale.value === 'de' ? nuxtDe : nuxtEn))
const { pageTitle } = useBreadcrumbs()
const { layoutState, isSidebarActive } = useLayout()
const { fetchUserWorkspaces, isAuthenticated, hasInternalAccess, profileIsIncomplete, userInfo } =
  useUser()
const { clearNotifications, initialize: initializeNotifications, stopPolling } = useNotifications()
const route = useRoute()
useBrowserTitle()

const outsideClickListener = ref<((event: Event) => void) | null>(null)

const containerClass = computed(() => ({
  'layout-static': true,
  'layout-static-inactive': layoutState.staticMenuDesktopInactive,
  'layout-overlay-active': layoutState.overlayMenuActive,
  'layout-mobile-active': layoutState.staticMenuMobileActive,
}))

const impersonatedUserLabel = computed(() => {
  if (!userInfo.value) {
    return ''
  }

  const fullName = `${userInfo.value.firstName ?? ''} ${userInfo.value.lastName ?? ''}`.trim()
  return fullName || userInfo.value.userName || userInfo.value.id || ''
})

function bindOutsideClickListener() {
  if (!outsideClickListener.value) {
    outsideClickListener.value = (event: Event) => {
      if (isOutsideClicked(event)) {
        layoutState.overlayMenuActive = false
        layoutState.staticMenuMobileActive = false
        layoutState.menuHoverActive = false
      }
    }
    document.addEventListener('click', outsideClickListener.value)
  }
}

function unbindOutsideClickListener() {
  if (outsideClickListener.value) {
    document.removeEventListener('click', outsideClickListener.value)
    outsideClickListener.value = null
  }
}

function isOutsideClicked(event: Event) {
  const sidebarEl = document.querySelector('.layout-sidebar')
  const topbarEl = document.querySelector('.layout-menu-button')
  if (!sidebarEl || !topbarEl) {
    return true
  }
  const target = event.target as Node
  return !(
    sidebarEl.isSameNode(target) ||
    sidebarEl.contains(target) ||
    topbarEl.isSameNode(target) ||
    topbarEl.contains(target)
  )
}

onMounted(() => {
  if (isAuthenticated.value && hasInternalAccess.value) {
    fetchUserWorkspaces(true)
  }

  if (isAuthenticated.value) {
    void initializeNotifications()
  }
})

onBeforeUnmount(() => {
  unbindOutsideClickListener()
  stopPolling()
})

watch(
  isAuthenticated,
  (authenticated) => {
    if (authenticated) {
      void initializeNotifications()
      return
    }

    stopPolling()
    clearNotifications()
  },
  { flush: 'sync' },
)

watch(
  () => isSidebarActive.value,
  (active) => {
    if (active) {
      bindOutsideClickListener()
    } else {
      unbindOutsideClickListener()
    }
  },
  { immediate: true },
)
</script>

<template>
  <UApp :locale="nuxtLocale">
    <div class="layout-wrapper" :class="containerClass">
      <AppTopbar />
      <section
        class="relative flex h-[var(--banner-h)] basis-full items-end bg-cover bg-center px-3 py-1 text-base text-white shadow-[0_5px_10px_rgba(0,0,0,0.3)] md:py-1.5 md:text-lg xl:p-2 xl:text-2xl"
        :style="{ backgroundImage: `url(${kitBannerUrl})` }"
        role="banner"
        fetchpriority="high"
      >
        {{ pageTitle?.includes('.') ? t(pageTitle) : pageTitle }}
      </section>
      <div class="layout-content-wrapper content-start align-top">
        <div>
          <!-- wrapped in div to prevent flex auto height of the side bar -->
          <div
            class="layout-sidebar z-0 flex h-auto min-w-64 flex-col overflow-y-auto rounded-br-lg border-r border-b border-neutral-200 bg-white p-4 transition-[transform_0.3s,left_0.3s,margin-left_0.3s] select-none dark:border-neutral-700 dark:bg-neutral-900"
          >
            <div class="mb-4">
              <img
                src="@/assets/images/signmeup_sbs.svg"
                alt="SignMeUp Logo"
                style="height: 2.4rem"
              />
              <EnvironmentIndicator />
            </div>

            <AppMenu />
            <!-- mt-auto pins the version to the bottom of the full-height mobile drawer while the
                 menu stays directly under the logo. -->
            <div class="mt-auto pt-8">
              <AppVersion />
            </div>
          </div>
        </div>
        <div class="layout-main-container min-h-full flex-1 transition-[margin_0.3s]">
          <div class="layout-main">
            <section
              v-if="isAuthenticated && userInfo?.isImpersonating"
              class="mb-2 rounded-lg border border-amber-300 bg-amber-100 px-4 py-2 text-amber-950 md:mb-3 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-50"
            >
              <p class="font-semibold">{{ t('components.impersonationBanner.title') }}</p>
              <p class="text-sm">
                {{
                  t('components.impersonationBanner.message', {
                    user: impersonatedUserLabel,
                    admin: userInfo.impersonatorDisplayName || t('common.labels.admin'),
                  })
                }}
              </p>
            </section>
            <section
              v-if="isAuthenticated && profileIsIncomplete"
              class="mb-2 bg-red-500 py-2 text-center text-white md:mb-3"
            >
              {{ t('pages.user.settings.profile.incompleteProfile') }}
              <router-link to="/user/settings/profile" class="font-bold text-white underline">
                {{ t('common.actions.completeProfile') }}
              </router-link>
            </section>
            <BreadcrumbBar v-if="!route.meta.hideBreadcrumb" />
            <router-view />
          </div>
        </div>
      </div>
      <div class="layout-mask animate-fadein" />
    </div>

    <GlobalLoader />

    <AppConfirmDialog />
  </UApp>
</template>
